// Endpoint catalog for the provider-side tarjouspyyntö surface.
// The transport is INJECTED so each FE keeps its own auth/error/telemetry stack:
//
//   transport = {
//     query:  (path, { fallback }) => Promise<data>,   // GET. MAY throw (betonijerry)
//                                                      // or resolve `fallback` on error (puminet4).
//     mutate: (path, { method, body }) => Promise<{ success, error?, data }>,
//   }
//
// This file owns path building + body shaping for BOTH frontends — a wire-shape
// change is a one-file edit here.

// Programmer-error guard: an undefined/null id would otherwise build
// silent "/undefined/" paths. Fail fast instead.
const reqd = (v, name) => {
    if (v == null) throw new TypeError(`${name} required`);
    return v;
};

export function createTarjouspyyntoApi({ query, mutate }) {
    return {
        listProviderRequests: (tab = "avoimet") =>
            query(`/api/pumppuRequests/provider-list?tab=${encodeURIComponent(tab)}`, { fallback: null }),

        getProviderCounts: () =>
            query("/api/pumppuRequests/provider-counts", { fallback: null }),

        getProviderDetail: (pumppuRequestId) =>
            query(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/provider-detail`, { fallback: null }),

        // PUBLIC tokenized read-only preview (email CTA landing). Call with a
        // TOKENLESS transport — the capability token in the query IS the auth.
        getPreview: (previewToken) =>
            query(`/api/pumppuRequests/preview?token=${encodeURIComponent(reqd(previewToken, "previewToken"))}`, { fallback: null }),

        // PUBLIC tokenized reveal of the customer's contact info on the preview
        // page. Call with a TOKENLESS transport — the capability token IS the
        // auth. Stamps the provider's view-claim (first reveal per company
        // emails the customer) and returns the revealed preview envelope.
        revealPreview: (previewToken) =>
            mutate("/api/pumppuRequests/preview/reveal", {
                method: "POST",
                body: { token: reqd(previewToken, "previewToken") },
            }),

        // PUBLIC tokenized BID from the preview page — no login (fb#1268).
        // Call with a TOKENLESS transport: the capability token IS the auth, and
        // it travels in the BODY because it now authorizes a write (a token in
        // the query string leaks into referrers, history and analytics).
        // Submitting the bid is what releases the customer's contact details, so
        // the resolved envelope carries the revealed preview DTO.
        offerViaPreview: (previewToken, body) =>
            mutate("/api/pumppuRequests/preview/offer", {
                method: "POST",
                body: { token: reqd(previewToken, "previewToken"), ...body },
            }),

        // PUBLIC tokenized DECLINE from the preview page — no login (fb#1268).
        // `reason` is required and is shown to the customer: a silent decline
        // tells them nothing, and until this existed the decline path was
        // login-gated and had never once been used.
        declineViaPreview: (previewToken, reason) => {
            const token = reqd(previewToken, "previewToken");
            // Not reqd(): that only rejects null/undefined, and a BLANK reason is
            // just as useless here — it is shown verbatim to the customer.
            if (typeof reason !== "string" || reason.trim() === "") {
                throw new TypeError("reason required");
            }
            return mutate("/api/pumppuRequests/preview/decline", {
                method: "POST",
                body: { token, reason: reason.trim() },
            });
        },

        // Post-login access check for the preview flow. Requires an
        // authenticated transport; returns { ok, asiakasId? } (never the denial case).
        checkPreviewAccess: (pumppuRequestId, previewToken) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/preview-access`, {
                method: "POST",
                body: { previewToken: reqd(previewToken, "previewToken") },
            }),

        submitOffer: (pumppuRequestId, body) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/offers`, { method: "POST", body }),

        sendOffer: (pumppuRequestId, pumppuOfferId) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/offers/${reqd(pumppuOfferId, "pumppuOfferId")}/send`, { method: "POST", body: {} }),

        confirmOffer: (pumppuRequestId, pumppuOfferId, body) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/offers/${reqd(pumppuOfferId, "pumppuOfferId")}/confirm`, { method: "POST", body }),

        // Provider declines the whole request (no offer) with an optional free-text
        // reason. Moves it out of the provider's Avoimet tab; notifies the customer.
        declineRequest: (pumppuRequestId, reason) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/decline`, { method: "POST", body: { reason: reason ?? null } }),

        // Reverse a prior decline — the request returns to Avoimet, offerable again.
        undeclineRequest: (pumppuRequestId) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/undecline`, { method: "POST", body: {} }),

        // Ensure the customer-chat thread exists so the operator can ask a question
        // BEFORE making an offer. Idempotent; returns { messageThreadId }.
        startConversation: (pumppuRequestId) =>
            mutate(`/api/pumppuRequests/${reqd(pumppuRequestId, "pumppuRequestId")}/thread`, { method: "POST", body: {} }),

        getJerryProviderSettings: () =>
            query("/api/jerry-provider-settings", { fallback: null }),

        saveJerryProviderSettings: (body) =>
            mutate("/api/jerry-provider-settings", { method: "PUT", body }),
    };
}
